import React, {
  useState,
  useRef,
  useCallback,
  createContext,
  useContext,
  cloneElement,
  Children,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { usePopper, PopperProps } from 'react-popper'
import { Portal } from 'react-portal'
import { useId } from '@reach/auto-id'

import { usePrevious } from '../../hooks/usePrevious'
import { theme } from '../../theme'
import { Box } from '../Box'
import { FocusLock } from '../FocusLock'
import { PseudoBox, PseudoBoxProps } from '../PseudoBox'
import { CloseButton, CloseButtonProps } from '../CloseButton'

///////////////////////////////////////////////////////

interface PopoutContextValue {
  triggerRef: React.RefObject<HTMLElement>
  popoutRef: React.RefObject<HTMLElement>
  backdropRef: React.RefObject<HTMLElement>
  placement: PopperProps<any>['placement']
  strategy: PopperProps<any>['strategy']
  modifiers: PopperProps<any>['modifiers']
  offset: [number, number]
  headerId: string
  bodyId: string
  popoutId: string
  onOpen: () => void
  onClose: () => void
  onToggle: () => void
  trigger: 'hover' | 'click'
  isOpen: boolean
  onBlur: React.FocusEventHandler<HTMLElement>
  closeOnEsc: boolean
  initialFocusRef: React.RefObject<HTMLElement>
  isHoveringRef: React.MutableRefObject<boolean>
  usePortal: boolean
  showBackdrop: boolean
  backdropColor: string
  backdropTransition: string
  delay: number
}

const PopoutContext = createContext<PopoutContextValue>(undefined)

///////////////////////////////////////////////////////

export const usePopoutContext = (): PopoutContextValue | undefined => {
  const context = useContext(PopoutContext)

  if (context == null) {
    throw Error('usePopoutContext must be used within <Popout />')
  }

  return context
}

///////////////////////////////////////////////////////

const wrapEvent = (theirHandler, ourHandler) => event => {
  if (theirHandler) {
    theirHandler(event)
  }

  if (!event.defaultPrevented) {
    return ourHandler(event)
  }
}

///////////////////////////////////////////////////////

export const PopoutTrigger = ({ children }) => {
  const {
    popoutId,
    onToggle,
    trigger,
    onOpen,
    isOpen,
    onClose,
    isHoveringRef,
    triggerRef,
    delay,
  } = usePopoutContext()

  const child = Children.only(children)

  let eventHandlers = {}

  if (trigger === 'click') {
    eventHandlers = {
      onClick: wrapEvent(child.props.onClick, onToggle),
    }
  }

  const openTimeout = useRef(null)

  if (trigger === 'hover') {
    eventHandlers = {
      onFocus: wrapEvent(child.props.onFocus, onOpen),
      onKeyDown: wrapEvent(child.props.onKeyDown, event => {
        if (event.key === 'Escape') {
          setTimeout(onClose, delay)
        }
      }),
      onBlur: wrapEvent(child.props.onBlur, onClose),
      onMouseEnter: wrapEvent(child.props.onMouseEnter, () => {
        isHoveringRef.current = true
        openTimeout.current = setTimeout(onOpen, delay)
      }),
      onMouseLeave: wrapEvent(child.props.onMouseLeave, () => {
        isHoveringRef.current = false
        if (openTimeout.current) {
          clearTimeout(openTimeout.current)
          openTimeout.current = null
        }
        setTimeout(() => {
          if (isHoveringRef.current === false) {
            onClose()
          }
        }, delay)
      }),
    }
  }

  return cloneElement(child, {
    'aria-haspopup': 'dialog',
    ref: triggerRef,
    'aria-expanded': isOpen,
    'aria-controls': popoutId,
    ...eventHandlers,
  })
}

///////////////////////////////////////////////////////

export interface PopoutContentProps extends PseudoBoxProps {
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>
  onBlur?: React.FocusEventHandler<HTMLElement>
  onFocus?: React.FocusEventHandler<HTMLElement>
  onMouseLeave?: React.MouseEventHandler<HTMLElement>
  onMouseEnter?: React.MouseEventHandler<HTMLElement>
  children?: React.ReactNode
  noBlurClose?: boolean
}

export const PopoutContent = ({
  children,
  onKeyDown,
  onBlur: onBlurProp,
  onMouseLeave,
  onMouseEnter,
  onFocus,
  zIndex = theme.zIndices.popover,
  style,
  noBlurClose,
  ...restProps
}: PopoutContentProps) => {
  const {
    triggerRef,
    popoutRef,
    backdropRef,
    placement,
    popoutId,
    isOpen,
    onBlur,
    closeOnEsc,
    onClose,
    isHoveringRef,
    trigger,
    headerId,
    bodyId,
    showBackdrop,
    backdropColor,
    backdropTransition,
    strategy,
    modifiers,
    offset,
  } = usePopoutContext()

  const { styles, attributes } = usePopper(triggerRef.current, popoutRef.current, {
    placement,
    strategy,
    modifiers: offset ? [...(modifiers || []), { name: 'offset', options: { offset } }] : modifiers,
  })

  let eventHandlers = {}

  if (trigger === 'click') {
    eventHandlers = {
      onBlur: noBlurClose ? onBlurProp : wrapEvent(onBlurProp, onBlur),
    }
  }

  if (trigger === 'hover') {
    eventHandlers = {
      onMouseEnter: wrapEvent(onMouseEnter, () => {
        isHoveringRef.current = true
      }),
      onMouseLeave: wrapEvent(onMouseLeave, () => {
        isHoveringRef.current = false
        setTimeout(onClose, 300)
      }),
    }
  }

  eventHandlers = {
    ...eventHandlers,
    onKeyDown: wrapEvent(onKeyDown, event => {
      if (event.key === 'Escape' && closeOnEsc) {
        onClose && onClose()
      }
    }),
  }

  return (
    <Portal>
      {showBackdrop && isOpen && (
        <Box
          position="fixed"
          top={0}
          right={0}
          bottom={0}
          left={0}
          bg={backdropColor}
          onClick={onClose}
          zIndex={zIndex}
          ref={backdropRef}
          transition={backdropTransition}
        />
      )}

      <FocusLock>
        <PseudoBox
          ref={popoutRef}
          id={popoutId}
          // display={isOpen ? null : 'none'}
          opacity={isOpen ? 1 : 0}
          visibility={isOpen ? 'visible' : 'hidden'}
          zIndex={isOpen ? zIndex : -1}
          tabIndex={-1}
          _focus={{ outline: 0, boxShadow: 'outline' }}
          aria-hidden={!isOpen}
          aria-labelledby={headerId}
          aria-describedby={bodyId}
          role="dialog"
          style={{ ...styles.popper, ...style }}
          {...eventHandlers}
          {...attributes.popper}
          {...restProps}
        >
          {children}
        </PseudoBox>
      </FocusLock>
    </Portal>
  )
}

///////////////////////////////////////////////////////

export interface PopoutCloseButtonProps extends CloseButtonProps {}

export const PopoutCloseButton = forwardRef(
  ({ onClick, ...props }: PopoutCloseButtonProps, ref) => {
    const { onClose } = usePopoutContext()

    return (
      <CloseButton onClick={wrapEvent(onClick, onClose)} aria-label="Close" ref={ref} {...props} />
    )
  },
)

///////////////////////////////////////////////////////

export interface PopoutProps extends Partial<PopoutContextValue> {
  closeOnBlur?: boolean
  defaultIsOpen?: boolean
  returnFocusOnClose?: boolean
  children?: React.ReactNode
  id?: string
}

export const Popout = forwardRef(
  (
    {
      placement,
      strategy,
      modifiers,
      offset,
      initialFocusRef,
      showBackdrop = true,
      backdropColor = 'blackAlpha.300',
      backdropTransition,
      children,
      id,
      isOpen: isOpenProp,
      usePortal = true,
      trigger = 'click',
      closeOnBlur = true,
      closeOnEsc = true,
      onOpen,
      onClose,
      defaultIsOpen = false,
      returnFocusOnClose = true,
      delay = 300,
    }: PopoutProps,
    ref,
  ) => {
    const [isOpen, setOpen] = useState(defaultIsOpen)
    const { current: isControlled } = useRef(isOpenProp != null)

    const _isOpen = isControlled ? isOpenProp : isOpen

    const isHoveringRef = useRef<boolean | null>(null)
    const triggerRef = useRef(null)
    const popoutRef = useRef(null)
    const backdropRef = useRef(null)

    const onToggle = () => {
      if (!isControlled) {
        setOpen(!_isOpen)
      }

      if (!_isOpen === true) {
        window?.dispatchEvent(new Event('resize')) // Fix body scroll bar offsetting the popout
        onOpen && onOpen()
      } else {
        onClose && onClose()
      }
    }

    const handleOpen = useCallback(() => {
      setOpen(true)
      window?.dispatchEvent(new Event('resize')) // Fix body scroll bar offsetting the popout
      onOpen && onOpen()
    }, [setOpen, onOpen])

    const handleClose = useCallback(() => {
      setOpen(false)
      onClose && onClose()
    }, [setOpen, onClose])

    const handleBlur = event => {
      if (
        _isOpen &&
        closeOnBlur &&
        popoutRef.current &&
        triggerRef.current &&
        !popoutRef.current.contains(event.relatedTarget) &&
        !triggerRef.current.contains(event.relatedTarget) &&
        !backdropRef.current.contains(event.relatedTarget)
      ) {
        onClose && onClose()
      }
    }

    useImperativeHandle(ref, () => ({
      onOpen: handleOpen,
      onClose: handleClose,
      onToggle,
    }))

    // useOnClickOutside(popoutRef, handleClose)

    // A unique fallback id in case the id prop wasn't passed
    const fallbackId = `popout-${useId()}`
    const popoutId = id || fallbackId

    const headerId = `${popoutId}-header`
    const bodyId = `${popoutId}-body`

    const prevIsOpen = usePrevious(_isOpen)

    useEffect(() => {
      if (_isOpen && trigger === 'click') {
        setTimeout(() => {
          if (initialFocusRef && initialFocusRef.current) {
            initialFocusRef.current.focus()
          } else {
            if (popoutRef.current) {
              popoutRef.current.focus()
            }
          }
        }, 50)
      }

      if (!_isOpen && prevIsOpen && trigger === 'click' && returnFocusOnClose) {
        if (triggerRef.current) {
          triggerRef.current.focus()
        }
      }
    }, [_isOpen, popoutRef, initialFocusRef, trigger, triggerRef, prevIsOpen, returnFocusOnClose])

    const context: PopoutContextValue = {
      triggerRef,
      popoutRef,
      backdropRef,
      placement,
      headerId,
      bodyId,
      popoutId,
      onOpen: handleOpen,
      onClose: handleClose,
      onToggle,
      trigger,
      isOpen: _isOpen,
      onBlur: handleBlur,
      closeOnEsc,
      initialFocusRef,
      isHoveringRef,
      usePortal,
      showBackdrop,
      backdropColor,
      backdropTransition,
      strategy,
      modifiers,
      offset,
      delay,
    }

    return (
      <PopoutContext.Provider value={context}>
        {typeof children === 'function'
          ? children({ isOpen: _isOpen, onClose: handleClose })
          : children}
      </PopoutContext.Provider>
    )
  },
)

export default Popout
